import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  ChatStates,
  selectChatState,
  virtualHumanTalkingSelector,
} from '../../../../slices/chat/ChatSlice';
import RecordingButton from '../../RecordingButton/RecordingButton';
import * as Styled from './DeepgramButtonRecordAndSend.styled';
import { languageSelector } from '../../../../slices/PatientSlice';
import { MicMutedContext } from '../../MicMutedProvider.context';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { useUpdateMicActive } from '@vh/components/VHChatInputArea/VHTextChatInput/useUpdateMicActive';
import { useDeepgramTranscribeRecordedAudio } from '@vh/components/VHChatInputArea/VHTextChatInput/DeepgramButtonRecordAndSend/useDeepgramTranscribeRecordedAudio';
import useGetDeepgramToken from '@vh/utility/useGetDeepgramToken';
import useFlashMessage from '@core/components/FlashMessage';

/**
 * @type {import('react').FC<{
 *   onSubmit: (chat: string) => void;
 *   onChange: (e: string) => void;
 *   textDisabled: boolean;
 *   recordingDisabled: boolean;
 *   onSpeechError: (err: string) => void;
 *   currentChat: string;
 *   onPendingTranscription: (pending: boolean) => void;
 *   design: "fill" | "outline" | "transparent" | "text";
 *   recording: boolean;
 *   setRecording: (recording: boolean) => void;
 *   onConnectionChange?: (isConnected: boolean) => void;
 * }>}
 */

const DeepgramButtonRecordAndSend = ({
  onSubmit,
  onChange,
  textDisabled,
  recordingDisabled,
  onSpeechError,
  currentChat,
  onPendingTranscription,
  design,
  recording,
  setRecording,
  onConnectionChange,
}) => {
  const { muted, setMuted } = useContext(MicMutedContext);
  const { setVolume, stream } = useContext(MicrophoneStreamContext);
  const chatState = useSelector(selectChatState);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const language = useSelector(languageSelector);
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const showMutedNoticeIfTalked =
    muted &&
    !virtualHumanTalking &&
    chatLoadingStatus !== 'pending' &&
    chatState !== ChatStates.LOADING;

  const sttToken = useGetDeepgramToken();
  const { start, send, error } = useDeepgramTranscribeRecordedAudio(
    sttToken,
    stream,
    language?.BCP47Code ?? 'en-US'
  );

  const [sending, setSending] = useState(false);
  const { addFlashMessage } = useFlashMessage();
  const handleRecordingButtonClick = useCallback(async () => {
    if (recording) {
      setSending(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        setVolume(0);
        setMuted(true);

        const response = await send();
        const processedResponse = await response?.json();
        const text =
          processedResponse?.results?.channels?.[0]?.alternatives?.[0]
            .transcript;
        if (text) {
          const newVal = `${currentChat.trim()}${
            currentChat?.length > 0 && text?.length > 0 ? ' ' : ''
          }${text.trim()}`;
          onChange(newVal);
        } else {
          addFlashMessage(
            `Speech in ${
              language?.name ?? 'English'
            } could not be detected, please try again.`,
            'warning'
          );
        }
        setSending(false);
      } catch (e) {
        setSending(false);
      }
    } else {
      setMuted(false);
      setVolume(1);
      start();
    }
    setRecording(!recording);
  }, [
    recording,
    setRecording,
    setVolume,
    setMuted,
    send,
    onChange,
    currentChat,
    addFlashMessage,
    language?.name,
    start,
  ]);

  useUpdateMicActive(recording);

  // For record-and-send mode, we're always "connected" since we don't use live streaming
  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(true); // Always connected for record-and-send mode
    }
  }, [onConnectionChange]);

  return (
    <RecordingButton
      disabled={recordingDisabled || sending}
      onClick={handleRecordingButtonClick}
      muted={muted}
      connected={false}
      showMutedNoticeIfTalked={showMutedNoticeIfTalked}
      networkError={error}
      recording={recording}
      connecting={false}
      design={design}
      loading={sending}
    />
  );
};

export default DeepgramButtonRecordAndSend;
